// src/features/home/components/Sidebar.tsx

import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Sidebar.module.scss';
import SocialLinks from './SocialLinks';
import ShinyText from '../../../shared/components/ShinyText';
import { motion } from 'framer-motion';

interface SidebarProps {
  aboutRef: React.RefObject<HTMLDivElement>;
  experienceRef: React.RefObject<HTMLDivElement>;
  projectsRef: React.RefObject<HTMLDivElement>;
  activeSection: string;
  scrollToSection: (ref: React.RefObject<HTMLDivElement>) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  aboutRef,
  experienceRef,
  projectsRef,
  activeSection,
  scrollToSection,
}) => {
  const { t } = useTranslation();

  // Define animation variants for Sidebar
  const sidebarVariants = {
    hidden: { x: -50, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  // Define animation variants for navigation items
  const navItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: { delay: i * 0.1, duration: 0.5 },
    }),
  };

  // Animation variant for the hire me button
  const buttonVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { delay: 0.4, duration: 0.5 },
    },
  };

  const navItems = [
    { label: 'about', ref: aboutRef },
    { label: 'experience', ref: experienceRef },
    { label: 'projects', ref: projectsRef },
  ];

  return (
    <motion.aside
      className={styles.sidebar}
      initial="hidden"
      animate="visible"
      variants={sidebarVariants}
    >
      <div className={styles.intro}>
        <h1 className={styles.name}>Jamil Joo</h1>
        <p className={styles.shortBio}>{t('home.shortBio')}</p>
      </div>
      <nav className={styles.nav}>
        <ul>
          {navItems.map((item, index) => (
            <motion.li
              key={item.label}
              className={activeSection === item.label ? styles.active : ''}
              onClick={() => scrollToSection(item.ref)}
              custom={index}
              initial="hidden"
              animate="visible"
              variants={navItemVariants}
              whileHover={{ scale: 1.05 }}
            >
              <span className={styles.hyphen}></span>
              {t(`navigation.${item.label}`)}
            </motion.li>
          ))}
        </ul>
      </nav>

      <SocialLinks />
    </motion.aside>
  );
};

export default Sidebar;